import { Action } from 'redux';
import Service, {
    Meta,
    BusinessAreaType,
    DaycareService,
    BoardingService,
    TransportationService,
    TrainingService,
    WalkingService
} from 'model/Service';
import { Vehicle } from '@spike/vehicle-model';

//Action Types
export const SERVICES_RESET_ACTION_TYPE = 'SERVICES_RESET';
export const SERVICES_CLEAR_ACTION_TYPE = 'SERVICES_CLEAR';
export const SERVICES_FETCH_START_ACTION_TYPE = 'SERVICES_FETCH_START';
export const SERVICES_FETCH_SUCCESS_ACTION_TYPE = 'SERVICES_FETCH_SUCCESS';
export const SERVICES_FETCH_EVALUATION_START_ACTION_TYPE = 'SERVICES_FETCH_EVALUATION_START';
export const SERVICES_FETCH_EVALUATION_SUCCESS_ACTION_TYPE = 'SERVICES_FETCH_EVALUATION_SUCCESS';
export const SERVICES_SAVE_START_ACTION_TYPE = 'SERVICES_SAVE_START';
export const SERVICES_SAVE_SUCCESS_ACTION_TYPE = 'SERVICES_SAVE_SUCCESS';
export const SERVICES_SAVE_BULK_START_ACTION_TYPE = 'SERVICES_SAVE_BULK_START';
export const SERVICES_SAVE_BULK_SUCCESS_ACTION_TYPE = 'SERVICES_SAVE_BULK_SUCCESS';
export const SERVICES_DELETE_START_ACTION_TYPE = 'SERVICES_DELETE_START';
export const SERVICES_DELETE_SUCCESS_ACTION_TYPE = 'SERVICES_DELETE_SUCCESS';
export const SERVICES_ACTIVATE_START_ACTION_TYPE = 'SERVICES_ACTIVATE_START';
export const SERVICES_ACTIVATE_SUCCESS_ACTION_TYPE = 'SERVICES_ACTIVATE_SUCCESS';
export const SERVICES_INACTIVATE_START_ACTION_TYPE = 'SERVICES_INACTIVATE_START';
export const SERVICES_INACTIVATE_SUCCESS_ACTION_TYPE = 'SERVICES_INACTIVATE_SUCCESS';
export const SERVICES_PET_TYPES_UPDATE_START_ACTION_TYPE = 'SERVICES_PET_TYPE_UPDATE_START';
export const SERVICES_PET_TYPES_UPDATE_SUCCESS_ACTION_TYPE = 'SERVICES_PET_TYPE_UPDATE_SUCCESS';
export const SERVICES_BUSINESS_AREA_UPDATE_START_ACTION_TYPE = 'SERVICES_BUSINESS_AREA_UPDATE_START';
export const SERVICES_BUSINESS_AREA_UPDATE_SUCCESS_ACTION_TYPE = 'SERVICES_BUSINESS_AREA_UPDATE_SUCCESS';
export const SERVICES_STAFF_UPDATE_START_ACTION_TYPE = 'SERVICES_STAFF_UPDATE_START';
export const SERVICES_STAFF_UPDATE_SUCCESS_ACTION_TYPE = 'SERVICES_STAFF_UPDATE_SUCCESS';
export const SERVICES_DAYCARE_FETCH_START_ACTION_TYPE = 'SERVICES_DAYCARE_FETCH_START';
export const SERVICES_DAYCARE_FETCH_SUCCESS_ACTION_TYPE = 'SERVICES_DAYCARE_FETCH_SUCCESS';
export const SERVICES_BOARDING_FETCH_START_ACTION_TYPE = 'SERVICES_BOARDING_FETCH_START';
export const SERVICES_BOARDING_FETCH_SUCCESS_ACTION_TYPE = 'SERVICES_BOARDING_FETCH_SUCCESS';
export const SERVICES_TRANSPORTATION_FETCH_START_ACTION_TYPE = 'SERVICES_TRANSPORTATION_FETCH_START';
export const SERVICES_TRANSPORTATION_FETCH_SUCCESS_ACTION_TYPE = 'SERVICES_TRANSPORTATION_FETCH_SUCCESS';
export const SERVICES_TRAINING_FETCH_START_ACTION_TYPE = 'SERVICES_TRAINING_FETCH_START';
export const SERVICES_TRAINING_FETCH_SUCCESS_ACTION_TYPE = 'SERVICES_TRAINING_FETCH_SUCCESS';
export const SERVICES_WALKING_FETCH_START_ACTION_TYPE = 'SERVICES_WALKING_FETCH_START';
export const SERVICES_WALKING_FETCH_SUCCESS_ACTION_TYPE = 'SERVICES_WALKING_FETCH_SUCCESS';
export const SERVICES_BOARDING_OFFERS_FETCH_START_ACTION_TYPE = 'SERVICES_BOARDING_OFFERS_FETCH_START';
export const SERVICES_BOARDING_OFFERS_FETCH_SUCCESS_ACTION_TYPE = 'SERVICES_BOARDING_OFFERS_FETCH_SUCCESS';
export const SERVICES_ERROR_ACTION_TYPE = 'SERVICES_ERROR';

//Actions
type ServicesResetAction = Action<typeof SERVICES_RESET_ACTION_TYPE>;
type ServicesClearAction = Action<typeof SERVICES_CLEAR_ACTION_TYPE>;

// Fetch start action with business area context
interface ServicesFetchStartAction extends Action<typeof SERVICES_FETCH_START_ACTION_TYPE> {
    payload?: {
        businessArea?: BusinessAreaType;
    };
}

interface ServicesFetchSuccessAction extends Action<typeof SERVICES_DAYCARE_FETCH_SUCCESS_ACTION_TYPE> {
    payload: {
        daycareServices: Array<DaycareService>;
    };
}

interface ServicesDaycareSuccessAction extends Action<typeof SERVICES_FETCH_SUCCESS_ACTION_TYPE> {
    payload: {
        services: Array<Service>;
        meta: Meta;
        duplicatedId?: number;
        businessArea?: BusinessAreaType; //Business area context for segmented state
    };
}

interface ServicesBoardingFetchSuccessAction extends Action<typeof SERVICES_BOARDING_FETCH_SUCCESS_ACTION_TYPE> {
    payload: {
        boardingServices: Array<BoardingService>;
    };
}

interface ServicesBoardingOffersFetchSuccessAction
    extends Action<typeof SERVICES_BOARDING_OFFERS_FETCH_SUCCESS_ACTION_TYPE> {
    payload: {
        boardingServices: Array<BoardingService>;
    };
}

interface ServicesTransportationFetchSuccessAction
    extends Action<typeof SERVICES_TRANSPORTATION_FETCH_SUCCESS_ACTION_TYPE> {
    payload: {
        transportationServices: Array<TransportationService>;
    };
}

interface ServicesTrainingFetchSuccessAction extends Action<typeof SERVICES_TRAINING_FETCH_SUCCESS_ACTION_TYPE> {
    payload: {
        trainingServices: Array<TrainingService>;
    };
}

interface ServicesWalkingFetchSuccessAction extends Action<typeof SERVICES_WALKING_FETCH_SUCCESS_ACTION_TYPE> {
    payload: {
        walkingServices: Array<WalkingService>;
    };
}

// Evaluation
type ServicesFetchEvaluationStartAction = Action<typeof SERVICES_FETCH_EVALUATION_START_ACTION_TYPE>;

interface ServicesFetchEvaluationSuccessAction extends Action<typeof SERVICES_FETCH_EVALUATION_SUCCESS_ACTION_TYPE> {
    payload: {
        evaluationService: Service | null;
    };
}

type ServicesSaveStartAction = Action<typeof SERVICES_SAVE_START_ACTION_TYPE>;

interface ServicesSaveSuccessAction extends Action<typeof SERVICES_SAVE_SUCCESS_ACTION_TYPE> {
    payload: {
        service: Service;
    };
}

type ServicesSaveBulkStartAction = Action<typeof SERVICES_SAVE_BULK_START_ACTION_TYPE>;

type ServicesSaveBulkSuccessAction = Action<typeof SERVICES_SAVE_BULK_SUCCESS_ACTION_TYPE>;

type ServicesDeleteStartAction = Action<typeof SERVICES_DELETE_START_ACTION_TYPE>;

type ServicesDeleteSuccessAction = Action<typeof SERVICES_DELETE_SUCCESS_ACTION_TYPE>;

type ServicesActivateStartAction = Action<typeof SERVICES_ACTIVATE_START_ACTION_TYPE>;

type ServicesActivateSuccessAction = Action<typeof SERVICES_ACTIVATE_SUCCESS_ACTION_TYPE>;

type ServicesInactivateStartAction = Action<typeof SERVICES_INACTIVATE_START_ACTION_TYPE>;

type ServicesInactivateSuccessAction = Action<typeof SERVICES_INACTIVATE_SUCCESS_ACTION_TYPE>;

type ServicesPetTypesUpdateStartAction = Action<typeof SERVICES_PET_TYPES_UPDATE_START_ACTION_TYPE>;

type ServicesPetTypesUpdateSuccessAction = Action<typeof SERVICES_PET_TYPES_UPDATE_SUCCESS_ACTION_TYPE>;

type ServicesBusinessAreaUpdateStartAction = Action<typeof SERVICES_BUSINESS_AREA_UPDATE_START_ACTION_TYPE>;

type ServicesBusinessAreaUpdateSuccessAction = Action<typeof SERVICES_BUSINESS_AREA_UPDATE_SUCCESS_ACTION_TYPE>;

type ServicesStaffUpdateStartAction = Action<typeof SERVICES_STAFF_UPDATE_START_ACTION_TYPE>;

type ServicesStaffUpdateSuccessAction = Action<typeof SERVICES_STAFF_UPDATE_SUCCESS_ACTION_TYPE>;

type ServicesDaycareStartAction = Action<typeof SERVICES_DAYCARE_FETCH_START_ACTION_TYPE>;

type ServicesBoardingStartAction = Action<typeof SERVICES_BOARDING_FETCH_START_ACTION_TYPE>;

type ServicesBoardingOffersStartAction = Action<typeof SERVICES_BOARDING_OFFERS_FETCH_START_ACTION_TYPE>;

type ServicesTransportationStartAction = Action<typeof SERVICES_TRANSPORTATION_FETCH_START_ACTION_TYPE>;

type ServicesTrainingStartAction = Action<typeof SERVICES_TRAINING_FETCH_START_ACTION_TYPE>;

type ServicesWalkingStartAction = Action<typeof SERVICES_WALKING_FETCH_START_ACTION_TYPE>;

type ServicesErrorAction = Action<typeof SERVICES_ERROR_ACTION_TYPE>;

export type ServicesAction =
    | ServicesResetAction
    | ServicesClearAction
    | ServicesFetchStartAction
    | ServicesFetchSuccessAction
    | ServicesFetchEvaluationStartAction
    | ServicesFetchEvaluationSuccessAction
    | ServicesSaveStartAction
    | ServicesSaveSuccessAction
    | ServicesSaveBulkStartAction
    | ServicesSaveBulkSuccessAction
    | ServicesDeleteStartAction
    | ServicesDeleteSuccessAction
    | ServicesActivateStartAction
    | ServicesActivateSuccessAction
    | ServicesInactivateStartAction
    | ServicesInactivateSuccessAction
    | ServicesPetTypesUpdateStartAction
    | ServicesPetTypesUpdateSuccessAction
    | ServicesBusinessAreaUpdateStartAction
    | ServicesBusinessAreaUpdateSuccessAction
    | ServicesStaffUpdateStartAction
    | ServicesStaffUpdateSuccessAction
    | ServicesDaycareStartAction
    | ServicesDaycareSuccessAction
    | ServicesBoardingStartAction
    | ServicesBoardingFetchSuccessAction
    | ServicesTransportationStartAction
    | ServicesTransportationFetchSuccessAction
    | ServicesTrainingStartAction
    | ServicesTrainingFetchSuccessAction
    | ServicesWalkingStartAction
    | ServicesWalkingFetchSuccessAction
    | ServicesBoardingOffersStartAction
    | ServicesBoardingOffersFetchSuccessAction
    | ServicesErrorAction;
